import React, { useState, useEffect } from 'react';

import { useToasts } from 'react-toast-notifications';

import CustomUpload from '@components/common/CustomUpload/CustomUpload';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import Spinner from '@components/common/Spinner/Spinner';
import Text from '@components/common/Text/Text';
import { createPotCategoryModel } from '@models/PotModel/createPotCategoryModel';
import { personRoles } from '@models/PotModel/personRoles';
import { statusPO } from '@models/PotModel/statusPO';
import { potType } from '@propTypes/potTypes';
import PotService from '@services/domain/Lpc/PotService';

import css from './PotImageUpload.module.scss';

type PotImageUploadProps = {
    image?: string;
    newPoolObjective?: (potObjectiveType) => void;
    potRef: string;
    personRole: string;
    potStatus: string;
    hidePoolImage: boolean;
    category: string;
    isOffered: boolean;
    mobileView: boolean;
    pot?: potType;
    openSetting?: boolean;
};

const PotImageUpload = ({
    image,
    potRef,
    personRole,
    potStatus,
    hidePoolImage,
    category,
    isOffered,
    mobileView,
}: PotImageUploadProps) => {
    const { t } = useTranslateLpc(['lpc-pot', 'common']);
    const { addToast } = useToasts();

    const [isLoadingImg, setIsLoadingImg] = useState(false);
    const [initialImage, setInitialImage] = useState(image);
    const [uploadedImage, setUploadedImage] = useState<boolean>(false);
    const [removeImage, setRemoveImage] = useState<boolean>(false);
    const [onActiveUpload, setOnActiveUpload] = useState(false);
    const onSubmitUploadImg = (image) => {
        setIsLoadingImg(true);
        return PotService.uploadPotImage(potRef, image)
            .then((img) => setInitialImage(img))
            .catch((err) => err)
            .finally(() => setIsLoadingImg(false));
    };

    const removeUploadedImage = () => {
        PotService.removePotImage(potRef)
            .then((res) => {
                if (res.status === 200) {
                    addToast(t(`success.UPDATE_SUCCESS`), {
                        appearance: 'success',
                        autoDismiss: true,
                    });
                    setInitialImage(`/front-static/images/pot/categories/${category}.png`);
                    setUploadedImage(false);
                }
            })
            .catch(() =>
                addToast(t(`errors.DEFAULT_ERROR_MESSAGE`), {
                    appearance: 'error',
                    autoDismiss: true,
                }),
            );
    };
    const activeUpload = (value) => {
        setOnActiveUpload(value);
    };

    const wrapperStyle = () => {
        let className = css.wrapper;
        personRole !== personRoles.ORGANISER && (className += ' ' + css.wrapper__disabled);

        isOffered && (className += ' ' + css.wrapper__offered);

        potStatus === statusPO.CLOSED &&
            personRole !== personRoles.BENEFICIARY &&
            !isOffered &&
            (className += ' ' + css.wrapper__closed);

        return className;
    };

    useEffect(() => {
        const defaultImage = createPotCategoryModel
            .filter((category) => category.value !== 'SOLIDARITY_POOL')
            .some((category) => initialImage?.includes(category.value));
        setInitialImage(defaultImage ? `/front-static/images/pot/categories/${category}.png` : initialImage);
        setUploadedImage(!defaultImage);
    }, [category, initialImage]);

    return (
        <>
            <CustomUpload
                onActiveUpload={onActiveUpload}
                inputName={'potImage'}
                fileSizeLimit={20000000}
                fileWidthLimit={20000000}
                fileHeightLimit={20000000}
                format={'.png,.jpg,.jpeg,.webp,.tiff,.svg,.gif'}
                onUploadImg={(image) => onSubmitUploadImg(image)}
                personRole={personRole}
            >
                {initialImage ? (
                    <div
                        className={wrapperStyle()}
                        onMouseEnter={() => uploadedImage && setRemoveImage(true)}
                        onMouseLeave={() => uploadedImage && setRemoveImage(false)}
                    >
                        <div
                            className={`${css.container} ${css.container__with_image} ${
                                hidePoolImage && css.container__disabled
                            }`}
                            style={{ backgroundImage: `url(${initialImage})` }}
                            onClick={() => activeUpload(true)}
                        >
                            {isLoadingImg ? (
                                <Spinner />
                            ) : (
                                <>
                                    {personRole === personRoles.ORGANISER && !hidePoolImage && (
                                        <div className={css.container__with_image__content}>
                                            <>
                                                <img
                                                    className={css.icon}
                                                    src="/front-static/icons/action/upload-white.svg"
                                                    alt="upload"
                                                />
                                                <h5>{t('image.image-replace-label')}</h5>
                                                <Text variant={'caption_01'} color={'white'}>
                                                    {t('image.image-format')}
                                                </Text>
                                            </>
                                        </div>
                                    )}
                                </>
                            )}
                            {personRole == personRoles.ORGANISER && uploadedImage && (removeImage || mobileView) && (
                                <div className={css.container__remove_image}>
                                    <img
                                        onClick={(event) => {
                                            removeUploadedImage();
                                            event.preventDefault();
                                        }}
                                        className={css.container__remove_image}
                                        src="/front-static/icons/navigation/close-white.svg"
                                        alt="close"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <>
                        {personRole === personRoles.ORGANISER && (
                            <div
                                onClick={() => activeUpload(true)}
                                className={`${css.container} ${css.container__no_image}`}
                            >
                                {isLoadingImg ? (
                                    <Spinner />
                                ) : (
                                    <div className={css.container__no_image__content}>
                                        <img
                                            className={css.icon}
                                            src="/front-static/icons/action/upload.svg"
                                            alt="upload"
                                        />
                                        <h5>{t('image.image-add-label')}</h5>
                                        <Text variant={'caption_01'} color={'txt-secondary'}>
                                            {t('image.image-format')}
                                        </Text>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </CustomUpload>
        </>
    );
};
export default PotImageUpload;
